import React, { useEffect, useRef } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { useGoogleMapsReady } from '@/hooks/useGoogleMapsReady';

interface AddressPinMapProps {
  center: { lat: number; lng: number } | null;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
}

/**
 * Read-only confirmation that an address resolved to a real place.
 *
 * Typing an address into a form gives no signal about whether it landed
 * somewhere sensible, and a home address that geocodes to the wrong town
 * silently poisons every route built from it.
 */
export const AddressPinMap: React.FC<AddressPinMapProps> = ({
  center,
  isLoading = false,
  emptyMessage = 'Your location will appear here once the address is filled in.',
  className = 'h-44',
}) => {
  const { isReady } = useGoogleMapsReady();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    if (!isReady || !center || !containerRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(containerRef.current, {
        center,
        zoom: 14,
        disableDefaultUI: true,
        gestureHandling: 'none',
        keyboardShortcuts: false,
        clickableIcons: false,
      });
    } else {
      mapRef.current.setCenter(center);
    }

    if (!markerRef.current) {
      markerRef.current = new google.maps.Marker({ map: mapRef.current, position: center });
    } else {
      markerRef.current.setPosition(center);
    }
  }, [isReady, center]);

  useEffect(
    () => () => {
      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapRef.current = null;
    },
    []
  );

  if (!center) {
    return (
      <div
        className={`flex ${className} flex-col items-center justify-center gap-2 rounded-lg border border-ink-200 bg-ink-50 px-4 text-center`}
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-ink-400" />
        ) : (
          <>
            <MapPin className="h-5 w-5 text-ink-300" />
            <p className="text-xs text-ink-500">{emptyMessage}</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label="Map showing the entered address"
      className={`${className} w-full overflow-hidden rounded-lg border border-ink-200`}
    />
  );
};
